import React, { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, Download, ExternalLink, CalendarDays, Apple } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { fetchPlans, downloadMyCalendar } from '../../lib/meApi';
import { DEFAULT_EVENT_IMAGE, handleEventImageError } from '../../utils/imageUrl';

const MONTHS_ES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
const DOW = ['Dom','Lun','Mar','Mié','Jue','Vie','Sáb'];

const fmtDayMonth = (d) => `${d.getDate()} ${MONTHS_ES[d.getMonth()].slice(0, 3).toLowerCase()}`;

const CalendarTab = () => {
  const [plans, setPlans] = useState(null);
  const [cursor, setCursor] = useState(() => {
    const d = new Date(); d.setDate(1); return d;
  });
  const [selectedDay, setSelectedDay] = useState(null);

  useEffect(() => {
    fetchPlans().then(setPlans).catch(() => setPlans([]));
  }, []);

  // Map yyyy-mm-dd → list of plans
  const eventsByDay = React.useMemo(() => {
    const m = {};
    if (!plans) return m;
    plans.forEach(p => {
      if (!p.event.start_date) return;
      const d = new Date(p.event.start_date);
      const key = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
      (m[key] = m[key] || []).push(p);
    });
    return m;
  }, [plans]);

  const today = new Date();
  const isSameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

  const firstDow = new Date(cursor.getFullYear(), cursor.getMonth(), 1).getDay();
  const daysInMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();

  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(cursor.getFullYear(), cursor.getMonth(), d));

  const prevMonth = () => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1));
  const nextMonth = () => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1));

  const handleDownload = async () => {
    try {
      await downloadMyCalendar();
      toast.success('Calendario descargado. Ábrelo para suscribirte ✨');
    } catch {
      toast.error('No se pudo descargar el calendario');
    }
  };

  // Selected day events
  const selectedKey = selectedDay
    ? `${selectedDay.getFullYear()}-${String(selectedDay.getMonth()+1).padStart(2,'0')}-${String(selectedDay.getDate()).padStart(2,'0')}`
    : null;
  const selectedEvents = selectedKey ? (eventsByDay[selectedKey] || []) : [];

  // Upcoming list (events from today onwards)
  const upcoming = (plans || [])
    .filter(p => p.event.start_date && new Date(p.event.start_date) >= new Date(today.toDateString()))
    .sort((a, b) => new Date(a.event.start_date) - new Date(b.event.start_date));

  if (plans === null) return <div className="text-center py-12 text-gray-400">Cargando…</div>;

  return (
    <div className="space-y-6" data-testid="me-calendar-content">
      {/* Header with month + export */}
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" onClick={prevMonth} data-testid="cal-prev-month" className="rounded-full">
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <h3 className="font-bold text-[#064E3B] text-lg w-44 text-center">
            {MONTHS_ES[cursor.getMonth()]} {cursor.getFullYear()}
          </h3>
          <Button variant="ghost" size="icon" onClick={nextMonth} data-testid="cal-next-month" className="rounded-full">
            <ChevronRight className="w-5 h-5" />
          </Button>
        </div>
        <Button
          onClick={handleDownload}
          variant="outline"
          className="rounded-full border-emerald-300 text-emerald-700 hover:bg-emerald-50"
          data-testid="cal-export-btn"
        >
          <Download className="w-4 h-4 mr-2" />
          Exportar a Google/Apple Calendar
        </Button>
      </div>

      {/* Calendar grid */}
      <div className="bg-white rounded-2xl border border-gray-100 p-2 sm:p-3">
        <div className="grid grid-cols-7 gap-1 sm:gap-1.5 mb-2">
          {DOW.map(d => (
            <div key={d} className="text-center text-[10px] uppercase tracking-wider text-gray-400 font-semibold py-1">
              {d}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1 sm:gap-1.5">
          {cells.map((d, i) => {
            if (!d) return <div key={i} />;
            const key = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
            const evs = eventsByDay[key] || [];
            const hasEvents = evs.length > 0;
            const isToday = isSameDay(d, today);
            const isSel = selectedDay && isSameDay(d, selectedDay);
            const firstEv = evs[0]?.event;
            return (
              <button
                key={i}
                type="button"
                onClick={() => setSelectedDay(isSel ? null : d)}
                data-testid={`cal-day-${key}`}
                className={`group relative aspect-square rounded-xl flex flex-col items-stretch text-sm transition-all overflow-hidden ${
                  isSel
                    ? 'bg-gradient-to-br from-[#00CC66] to-emerald-600 text-white shadow-lg ring-2 ring-emerald-400 scale-[1.02]'
                    : hasEvents
                      ? 'bg-gradient-to-br from-emerald-50 to-amber-50 text-[#064E3B] ring-2 ring-emerald-200 hover:ring-emerald-400 hover:shadow-md hover:scale-[1.03]'
                      : isToday
                        ? 'bg-emerald-50 text-[#064E3B] ring-1 ring-emerald-300'
                        : 'hover:bg-gray-50 text-gray-700'
                }`}
              >
                {/* Day number */}
                <div className="flex items-start justify-between px-1.5 pt-1.5">
                  <span className={`font-semibold leading-none ${isToday && !isSel ? 'font-extrabold text-emerald-700' : ''}`}>
                    {d.getDate()}
                  </span>
                  {hasEvents && (
                    <span
                      className={`min-w-[18px] h-[18px] sm:min-w-[20px] sm:h-5 px-1 rounded-full flex items-center justify-center text-[10px] sm:text-[11px] font-extrabold leading-none shadow-sm ${
                        isSel ? 'bg-white text-emerald-700' : 'bg-[#00CC66] text-white'
                      }`}
                      aria-label={`${evs.length} evento${evs.length > 1 ? 's' : ''}`}
                    >
                      {evs.length}
                    </span>
                  )}
                </div>

                {/* Event preview (first event name + colored bar) */}
                {hasEvents && firstEv && (
                  <div className="mt-auto px-1 pb-1 space-y-0.5">
                    <div
                      className={`text-[8px] sm:text-[9px] font-bold uppercase tracking-tight truncate rounded px-1 py-0.5 ${
                        isSel ? 'bg-white/25 text-white' : 'bg-emerald-500/90 text-white'
                      }`}
                      title={firstEv.name}
                    >
                      {firstEv.name}
                    </div>
                    {evs.length > 1 && (
                      <div className={`text-[8px] sm:text-[9px] font-semibold text-center ${isSel ? 'text-white/80' : 'text-emerald-700/80'}`}>
                        +{evs.length - 1} más
                      </div>
                    )}
                  </div>
                )}

                {/* Today indicator dot */}
                {isToday && !isSel && !hasEvents && (
                  <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-emerald-500" />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Selected day events */}
      {selectedDay && (
        <div data-testid="cal-day-events">
          <h4 className="font-bold text-[#064E3B] mb-2 text-sm">
            Eventos del {selectedDay.getDate()} de {MONTHS_ES[selectedDay.getMonth()]}
          </h4>
          {selectedEvents.length === 0 ? (
            <p className="text-sm text-gray-500 italic">Sin eventos este día.</p>
          ) : (
            <div className="space-y-2">
              {selectedEvents.map(p => (
                <a
                  key={p.event.id}
                  href={`/event/${p.event.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-3 bg-emerald-50 hover:bg-emerald-100 rounded-xl transition-colors"
                  data-testid={`cal-event-${p.event.id}`}
                >
                  <img src={p.event.main_image || DEFAULT_EVENT_IMAGE} alt={p.event.name} className="w-12 h-12 rounded-lg object-cover" onError={handleEventImageError} />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-[#064E3B] truncate">{p.event.name}</p>
                    <p className="text-[11px] text-gray-500 truncate">
                      {p.event.commune_name} {p.status === 'maybe' && '· Tal vez'}
                    </p>
                  </div>
                  <ExternalLink className="w-4 h-4 text-emerald-500 shrink-0" />
                </a>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Upcoming list */}
      <div>
        <h4 className="font-bold text-[#064E3B] mb-3 text-sm flex items-center gap-2">
          <CalendarDays className="w-4 h-4" /> Próximos eventos
        </h4>
        {upcoming.length === 0 ? (
          <p className="text-sm text-gray-500 italic text-center py-6 bg-gray-50 rounded-xl">
            No tienes eventos confirmados próximos. Marca eventos como &quot;Voy a ir&quot; para verlos aquí.
          </p>
        ) : (
          <div className="space-y-2">
            {upcoming.slice(0, 8).map(p => {
              const d = new Date(p.event.start_date);
              return (
                <a
                  key={p.event.id}
                  href={`/event/${p.event.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-3 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors"
                  data-testid={`cal-upcoming-${p.event.id}`}
                >
                  <div className="w-12 h-12 rounded-lg bg-white border border-emerald-200 flex flex-col items-center justify-center shrink-0">
                    <span className="text-[10px] uppercase font-bold text-emerald-600 leading-none">
                      {MONTHS_ES[d.getMonth()].slice(0,3)}
                    </span>
                    <span className="text-lg font-bold text-[#064E3B] leading-none">{d.getDate()}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-[#064E3B] truncate">{p.event.name}</p>
                    <p className="text-[11px] text-gray-500 truncate">
                      {p.event.commune_name} {p.status === 'maybe' && '· Tal vez'}
                    </p>
                  </div>
                </a>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default CalendarTab;
